import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchCourses,
  fetchEntitiesByGroup,
  fetchGroups,
  fetchPrerequisiteCandidates,
  type CourseSummary,
  type EntitySummary,
  type GroupNode,
  type PrerequisiteCandidate,
} from '../api/client.js';
import {
  buildCourseBrowserPath,
  buildEntityEditorPath,
  parseGroupId,
  readBrowserSelection,
  replaceCurrentBrowserSelection,
  writeBrowserSelection,
} from '../lib/authorNavigation.js';
import { readCourseParam } from '../lib/courseParam.js';
import { rememberCourse, resolveCourseSelection } from '../lib/courseSelection.js';

/**
 * ONE COURSE BROWSER PER COURSE, so a level switch cannot leave the previous level's content
 * interactive under the new level's heading.
 *
 * The same wrapper the work queue has had (`KeyedWorkQueueDashboard`), and for the same reason —
 * this screen's course lives in `useState`, reconciled from the URL by a passive effect, so on a
 * level change the header flips at once and the body does not. `gpt-5.6-sol` walked the window on
 * 2026-08-29: choosing Level III from the header while the Level I browser is mounted leaves Level I
 * groups, lessons and click handlers on screen under a Level III header, and clicking one of them
 * opens the LEVEL I lesson — the reviewer's "wires crossed" in its most literal form. Its
 * URL-normalising effect could also fire from that stale state and put the address bar back to
 * Level I, undoing the choice.
 *
 * Re-keying discards that state instead of reconciling it. The cost is a refetch of the course list
 * and group tree on a level change, which is the same fetch the reconciliation would have caused.
 *
 * **The reason the existing tests did not catch it:** they each mount directly at their destination
 * URL, so they never exercise a course CHANGE on an already-mounted browser — the identical
 * mounting-only blind spot that hid the queue's own defect. `CourseBrowser.test.tsx` now switches
 * the level on a live screen.
 */
export function KeyedCourseBrowser() {
  const [searchParams] = useSearchParams();
  return <CourseBrowser key={readCourseParam(searchParams) ?? ''} />;
}

export function CourseBrowser() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [groups, setGroups] = useState<GroupNode[]>([]);
  const [entitiesByGroup, setEntitiesByGroup] = useState<Record<number, EntitySummary[]>>({});
  const [loadingGroupIds, setLoadingGroupIds] = useState<Record<number, boolean>>({});
  const [groupEntityErrors, setGroupEntityErrors] = useState<Record<number, string>>({});
  const [groupRetryTokens, setGroupRetryTokens] = useState<Record<number, number>>({});
  const [lessonQuery, setLessonQuery] = useState('');
  const pastedLesson = parsePastedLessonUrl(lessonQuery);
  // A pasted URL naming a DIFFERENT course must not be answered with a same-numbered lesson from the
  // course that happens to be selected. Offer the real target instead of searching the wrong course.
  const pastedOtherCourse = pastedLesson && pastedLesson.courseId !== selectedCourseId ? pastedLesson : null;
  const [lessonResults, setLessonResults] = useState<PrerequisiteCandidate[] | null>(null);
  const [lessonSearching, setLessonSearching] = useState(false);
  const [lessonSearchError, setLessonSearchError] = useState('');
  const lessonSearchAbortRef = useRef<AbortController | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    void fetchCourses()
      .then((nextCourses) => {
        if (!cancelled) {
          setCourses(nextCourses);
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : 'Unable to load courses');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!courses.length) {
      return;
    }

    const storedSelection = readBrowserSelection();
    /*
     * `readCourseParam`, NOT `searchParams.get('courseId')`.
     *
     * This screen is the only one that spells the question `?courseId`; every other screen — and
     * `AppShell`, which draws the header above this one — resolves through `readCourseParam`, which
     * accepts the canonical `?courseKey` first and this screen's legacy alias second. Reading the
     * alias alone made the two disagree in both directions, confirmed in a real browser on
     * 2026-08-28: `/courses?courseKey=400` put a **Level III header over the Level I course
     * browser**, and `/courses?courseKey=12b&courseId=400` put a Level I header over Level III.
     * A header naming one exam level over another level's content is the exact wire-crossing the
     * reviewer asked us to prevent, and `/courses` was the last screen in the tool still doing it.
     *
     * The alias is still what gets WRITTEN (`buildCourseBrowserPath`), so every bookmark and every
     * link already pasted into Slack keeps working; converging on one written spelling is a separate,
     * deliberate change. See `lib/courseParam.ts`.
     */
    const requestedCourseId = readCourseParam(searchParams);
    /*
     * First visit, no `?courseId`, nothing stored: fall back to the AUTHORING course rather than to
     * whichever course happens to sort first. Measured in production 2026-08-17 — `courses[0]` is
     * the GMAT course, so the Academic Director opening this tab got a lesson search scoped to a
     * course he never works in, and a search for one of his own lessons returned nothing at all.
     * `selectAuthoringCourseKey` is the rule the rest of the app already uses (AppShell), so this
     * aligns rather than invents. An explicit `?courseId` and a stored selection both still win.
     */
    /*
     * ONE memory, shared with the header, and the SHARED one wins.
     *
     * This screen keeps its own `author.browser.selection` (course + group, under
     * `author.browser.selection`) and the shell keeps the level the reviewer last chose (under
     * `author.review.courseKey`). They are different keys and they go stale independently. Written
     * the other way round — legacy first — a reviewer who chose Level III in the header and had an
     * older browser selection for Level I got a bare `/courses` that **announced Level III in the
     * header, browsed Level I underneath, and then flipped the header to Level I**: his most recent
     * and most deliberate choice losing to stale state, with a visible flicker admitting it.
     *
     * So: the shared review course first, the legacy browser course only when there is no shared one
     * (which is what a first visit after this shipped looks like).
     */
    const shared = resolveCourseSelection(courses, null);
    const fallbackCourseId =
      // `source === 'remembered'` is the whole point of that field: it separates "the reviewer chose
      // this level" from "the application picked one because nobody had". Only a real choice may
      // outrank this screen's own memory; the app default must not, or a first visit would ignore
      // the course this screen was last left on.
      shared.source === 'remembered' ? shared.courseKey : storedSelection?.courseId ?? shared.courseKey;
    const nextCourseId = pickCourseId(courses, requestedCourseId, fallbackCourseId);
    if (nextCourseId !== selectedCourseId) {
      setSelectedCourseId(nextCourseId);
    }
  }, [courses, searchParams, selectedCourseId]);

  useEffect(() => {
    lessonSearchAbortRef.current?.abort();
    const term = lessonSearchTerm(lessonQuery);
    if (!selectedCourseId || term.length < 2) {
      setLessonResults(null);
      setLessonSearching(false);
      setLessonSearchError('');
      return;
    }

    setLessonResults(null);
    setLessonSearching(true);
    setLessonSearchError('');

    // This is deliberately coupled to `prerequisite-candidates`: it is the existing projected,
    // title-or-id lesson search, despite its editor-specific name. It excludes archived lessons
    // server-side, so absence here is not proof that a lesson never existed.
    const timer = setTimeout(() => {
      const controller = new AbortController();
      lessonSearchAbortRef.current = controller;
      void fetchPrerequisiteCandidates(selectedCourseId, term, controller.signal)
        .then((found) => {
          if (!controller.signal.aborted) {
            setLessonResults(found);
            setLessonSearching(false);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setLessonResults([]);
            setLessonSearching(false);
            setLessonSearchError('Unable to search lessons.');
          }
        });
    }, 250);

    return () => clearTimeout(timer);
  }, [lessonQuery, selectedCourseId]);

  useEffect(() => () => lessonSearchAbortRef.current?.abort(), []);

  useEffect(() => {
    if (!selectedCourseId) {
      setGroups([]);
      setSelectedGroupId(null);
      return;
    }

    let cancelled = false;
    setError('');
    setEntitiesByGroup({});
    setLoadingGroupIds({});
    setGroupEntityErrors({});
    setGroupRetryTokens({});

    void fetchGroups(selectedCourseId)
      .then((nextGroups) => {
        if (cancelled) {
          return;
        }

        setGroups(nextGroups);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : 'Unable to load course browser');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCourseId]);

  /**
   * THE ADDRESS BAR IS NORMALISED IN THE SAME COMMIT, NOT ONE FLUSH LATER — the identical fix, and
   * the identical defect, as the work queue's own URL sync (`WorkQueueDashboard.tsx`).
   *
   * This effect rewrites the address bar from THIS screen's `selectedCourseId`. As a passive effect
   * it was queued at commit and flushed after React yielded, and in that window the reviewer can
   * choose a different level from the header. What then happened, traced on 2026-08-29:
   *
   * 1. The reviewer is on `/courses?courseId=12b` with Level I's groups open.
   * 2. He picks Level III. The header navigates to `/courses?courseId=400`.
   * 3. This screen's queued sync flushes, built from the render before his choice, and REPLACES the
   *    address bar back to `courseId=12b&groupId=10`.
   *
   * The router therefore never observed Level III at all — no `key=400` render was ever committed —
   * so {@link KeyedCourseBrowser} could not discard the stale screen either, and Level I's groups and
   * lessons stayed on screen and clickable under a Level III header. `gpt-5.6-sol` predicted the
   * window on 2026-08-29 and the trace confirmed it swallows the navigation outright.
   *
   * A layout effect runs synchronously inside the commit, so there is no queued write for a later
   * navigation to land underneath. This is a URL normalisation only — nothing is fetched here.
   */
  useLayoutEffect(() => {
    if (!selectedCourseId) {
      return;
    }

    const flatGroups = flattenGroups(groups);
    if (!flatGroups.length) {
      setSelectedGroupId(null);
      return;
    }

    const storedSelection = readBrowserSelection();
    const requestedGroupId = parseGroupId(searchParams.get('groupId'));
    const fallbackGroupId =
      storedSelection?.courseId === selectedCourseId ? storedSelection.groupId : flatGroups[0]?.id ?? null;
    const nextGroupId = pickGroupId(flatGroups, requestedGroupId, fallbackGroupId);

    if (nextGroupId !== selectedGroupId) {
      setSelectedGroupId(nextGroupId);
      return;
    }

    syncBrowserSelection(selectedCourseId, selectedGroupId, searchParams, setSearchParams);
  }, [groups, searchParams, selectedCourseId, selectedGroupId, setSearchParams]);

  useEffect(() => {
    if (!selectedCourseId || selectedGroupId == null) {
      return;
    }

    if (entitiesByGroup[selectedGroupId] || loadingGroupIds[selectedGroupId]) {
      return;
    }

    let cancelled = false;
    setLoadingGroupIds((current) => ({ ...current, [selectedGroupId]: true }));
    setGroupEntityErrors((current) => {
      const { [selectedGroupId]: _removed, ...rest } = current;
      return rest;
    });

    void fetchEntitiesByGroup(selectedCourseId, selectedGroupId)
      .then((entities) => {
        if (!cancelled) {
          setEntitiesByGroup((current) => ({ ...current, [selectedGroupId]: entities }));
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setGroupEntityErrors((current) => ({
            ...current,
            [selectedGroupId]: reason instanceof Error ? reason.message : 'Unable to load entities for this group',
          }));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingGroupIds((current) => {
            const { [selectedGroupId]: _removed, ...rest } = current;
            return rest;
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [entitiesByGroup, groupRetryTokens, selectedCourseId, selectedGroupId]);

  useEffect(() => {
    if (selectedGroupId == null || typeof document === 'undefined') {
      return;
    }

    const target = document.querySelector<HTMLElement>(`[data-group-id="${selectedGroupId}"]`);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedGroupId]);

  function handleCourseChange(courseId: string) {
    setSelectedCourseId(courseId);
    setSelectedGroupId(null);
    // Picking a course here is the same act as picking a level in the header, so it updates the same
    // memory. Otherwise browsing Level III and then opening the queue would land on Level I.
    rememberCourse(courseId);
    syncBrowserSelection(courseId, null, searchParams, setSearchParams);
  }

  function handleGroupSelect(groupId: number) {
    setSelectedGroupId(groupId);
    syncBrowserSelection(selectedCourseId, groupId, searchParams, setSearchParams);
  }

  function handleEntityClick(groupId: number, entityId: number) {
    if (!selectedCourseId) {
      return;
    }

    setSelectedGroupId(groupId);
    writeBrowserSelection({ courseId: selectedCourseId, groupId });
    replaceCurrentBrowserSelection(selectedCourseId, groupId);
    navigate(buildEntityEditorPath(selectedCourseId, entityId, groupId));
  }

  function handleLessonSelect(entityId: number, courseOverride?: string) {
    const courseId = courseOverride ?? selectedCourseId;
    if (!courseId) {
      return;
    }
    void navigate(`/courses/${encodeURIComponent(courseId)}/entities/${entityId}/transcript`);
  }

  function handleRetryGroupEntities(groupId: number) {
    setEntitiesByGroup((current) => {
      const { [groupId]: _removed, ...rest } = current;
      return rest;
    });
    setLoadingGroupIds((current) => {
      const { [groupId]: _removed, ...rest } = current;
      return rest;
    });
    setGroupEntityErrors((current) => {
      const { [groupId]: _removed, ...rest } = current;
      return rest;
    });
    setGroupRetryTokens((current) => ({ ...current, [groupId]: (current[groupId] ?? 0) + 1 }));
  }

  return (
    <section className="grid gap-6">
      <div className="editor-panel grid gap-3 px-6 py-6">
        <div className="text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-slate-500">Author Workspace</div>
        <h2 className="font-serif text-[2rem] leading-none text-primary">Course Browser</h2>
        <p className="max-w-3xl text-sm leading-7 text-slate-600">
          Browse a course, inspect the group hierarchy, and open a single entity into the dedicated editor
          workspace.
        </p>
        <div className="relative grid max-w-3xl gap-1.5">
          <label className="text-sm font-semibold text-slate-800" htmlFor="lesson-search">
            Find a lesson in Courses
          </label>
          <input
            aria-describedby="lesson-search-help"
            autoComplete="off"
            className="w-full rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-primary/30 focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50"
            disabled={!selectedCourseId}
            id="lesson-search"
            onChange={(event) => setLessonQuery(event.target.value)}
            placeholder="Search by title or id, or paste a lesson URL…"
            type="search"
            value={lessonQuery}
          />
          <p className="text-xs leading-5 text-slate-500" id="lesson-search-help">
            Search the selected course. Archived lessons are not included.
          </p>
          {pastedOtherCourse ? (
            <button
              className="mt-1 w-full rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-left text-sm font-semibold text-amber-950 transition hover:bg-amber-100"
              onClick={() => handleLessonSelect(Number(pastedOtherCourse.entityId), pastedOtherCourse.courseId)}
              type="button"
            >
              That link is for course {pastedOtherCourse.courseId}, not {selectedCourseId ?? 'the selected course'}.
              Open lesson {pastedOtherCourse.entityId} in course {pastedOtherCourse.courseId} →
            </button>
          ) : null}
          {lessonSearching && !pastedOtherCourse ? <span className="text-xs font-medium text-slate-500">Searching…</span> : null}
          {lessonSearchError ? <span className="text-xs font-medium text-red-700">{lessonSearchError}</span> : null}
          {lessonResults && lessonResults.length === 0 && !lessonSearching && !lessonSearchError && !pastedOtherCourse ? (
            <span className="text-xs text-slate-500">No lesson matches that title or id.</span>
          ) : null}
          {lessonResults && lessonResults.length > 0 && !pastedOtherCourse ? (
            <div className="grid gap-1.5">
              <ul
                aria-label="Lesson search results"
                className="max-h-64 overflow-y-auto rounded-2xl border border-primary/10 bg-white p-1 shadow-lg"
              >
                {lessonResults.map((candidate) => (
                  <li key={candidate.id}>
                    <button
                      className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition hover:bg-primary/5"
                      onClick={() => handleLessonSelect(candidate.id)}
                      type="button"
                    >
                      <span className="min-w-0 truncate font-medium text-slate-800">{candidate.title}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        {candidate.published ? null : (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                            not published
                          </span>
                        )}
                        <span className="font-mono text-xs text-slate-500">#{candidate.id}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {lessonResults.length === 20 ? (
                <p className="text-xs font-medium text-amber-700">Showing the first 20; narrow the search.</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="editor-panel grid gap-5 px-6 py-6">
        <label className="grid gap-2 text-sm font-semibold text-slate-800" htmlFor="course-select">
          Course
          <select
            className="w-full max-w-[320px] rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-primary/30 focus:ring-0"
            id="course-select"
            onChange={(event) => handleCourseChange(event.target.value)}
            value={selectedCourseId}
          >
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </label>

        {error ? <div className="text-sm font-medium text-red-700">{error}</div> : null}

        <div className="grid gap-4">
          {groups.map((group) => (
            <GroupBranch
              entitiesByGroup={entitiesByGroup}
              group={group}
              groupEntityErrors={groupEntityErrors}
              loadingGroupIds={loadingGroupIds}
              key={group.id}
              onEntityClick={handleEntityClick}
              onGroupSelect={handleGroupSelect}
              onRetryEntities={handleRetryGroupEntities}
              selectedGroupId={selectedGroupId}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function GroupBranch({
  group,
  entitiesByGroup,
  loadingGroupIds,
  groupEntityErrors,
  onGroupSelect,
  onEntityClick,
  onRetryEntities,
  selectedGroupId,
}: {
  group: GroupNode;
  entitiesByGroup: Record<number, EntitySummary[]>;
  loadingGroupIds: Record<number, boolean>;
  groupEntityErrors: Record<number, string>;
  onGroupSelect: (groupId: number) => void;
  onEntityClick: (groupId: number, entityId: number) => void;
  onRetryEntities: (groupId: number) => void;
  selectedGroupId: number | null;
}) {
  const isSelected = selectedGroupId === group.id;

  return (
    <div
      className="grid gap-2.5 scroll-mt-28"
      data-group-id={group.id}
      data-selected={isSelected ? 'true' : 'false'}
    >
      <button
        className={`inline-flex w-fit items-center rounded-full px-3 py-1.5 text-sm font-semibold uppercase tracking-[0.1em] transition ${
          isSelected ? 'bg-primary text-cream shadow-sm' : 'text-slate-500 hover:bg-primary/5 hover:text-primary'
        }`}
        onClick={() => onGroupSelect(group.id)}
        type="button"
      >
        {group.title}
      </button>
      {isSelected && loadingGroupIds[group.id] ? (
        <div className="text-sm font-medium text-slate-500">Loading entities...</div>
      ) : null}
      {isSelected && groupEntityErrors[group.id] ? (
        <div className="flex w-fit items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
          <span>{groupEntityErrors[group.id]}</span>
          <button
            className="rounded border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
            onClick={() => onRetryEntities(group.id)}
            type="button"
          >
            Retry
          </button>
        </div>
      ) : null}
      {(entitiesByGroup[group.id] ?? []).map((entity) => (
        <button
          key={entity.id}
          onClick={() => onEntityClick(group.id, entity.id)}
          className="flex items-center justify-between gap-3 rounded-2xl border border-primary/10 bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary/20 hover:bg-primary/5"
          type="button"
        >
          <span className="font-medium text-slate-800">{entity.title}</span>
          <span className="rounded-full bg-primary/6 px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-slate-500">
            {entity.item_type_slug ?? 'ITEM'}
          </span>
        </button>
      ))}
      {group.children.map((child) => (
        <div key={child.id} className="ml-3 border-l border-primary/10 pl-4">
          <GroupBranch
            entitiesByGroup={entitiesByGroup}
            group={child}
            groupEntityErrors={groupEntityErrors}
            loadingGroupIds={loadingGroupIds}
            onEntityClick={onEntityClick}
            onGroupSelect={onGroupSelect}
            onRetryEntities={onRetryEntities}
            selectedGroupId={selectedGroupId}
          />
        </div>
      ))}
    </div>
  );
}

function flattenGroups(groups: GroupNode[]): GroupNode[] {
  return groups.flatMap((group) => [group, ...flattenGroups(group.children)]);
}

/**
 * A pasted lesson URL names BOTH a course and a lesson, and the course half is load-bearing.
 * Discarding it and searching the currently selected course means pasting a `12b` URL while `10b` is
 * selected silently opens `10b`'s lesson of the same number — a different lesson, which the reviewer
 * may then decide to delete. Entity ids are per-course and collide freely, so the course travels
 * with the id.
 */
function parsePastedLessonUrl(value: string): { courseId: string; entityId: string } | null {
  try {
    const pathname = new URL(value.trim(), 'https://course-browser.invalid').pathname;
    const match = pathname.match(/^\/courses\/([^/]+)\/entities\/(\d+)(?:\/transcript)?\/?$/);
    if (!match) return null;
    return { courseId: decodeURIComponent(match[1]!), entityId: match[2]! };
  } catch {
    return null;
  }
}

function lessonSearchTerm(value: string) {
  const trimmed = value.trim();
  return parsePastedLessonUrl(trimmed)?.entityId ?? trimmed;
}

function pickCourseId(courses: CourseSummary[], requestedCourseId: string | null, fallbackCourseId: string) {
  if (requestedCourseId && courses.some((course) => course.id === requestedCourseId)) {
    return requestedCourseId;
  }

  if (fallbackCourseId && courses.some((course) => course.id === fallbackCourseId)) {
    return fallbackCourseId;
  }

  return courses[0]?.id ?? '';
}

function pickGroupId(groups: GroupNode[], requestedGroupId: number | null, fallbackGroupId: number | null) {
  const groupIds = new Set(groups.map((group) => group.id));
  if (requestedGroupId != null && groupIds.has(requestedGroupId)) {
    return requestedGroupId;
  }

  if (fallbackGroupId != null && groupIds.has(fallbackGroupId)) {
    return fallbackGroupId;
  }

  return groups[0]?.id ?? null;
}

function syncBrowserSelection(
  courseId: string,
  groupId: number | null,
  searchParams: URLSearchParams,
  setSearchParams: ReturnType<typeof useSearchParams>[1],
) {
  writeBrowserSelection({ courseId, groupId });

  const nextPath = buildCourseBrowserPath(courseId, groupId);
  const currentPath = searchParams.toString() ? `/courses?${searchParams.toString()}` : '/courses';
  if (nextPath === currentPath) {
    return;
  }

  setSearchParams(new URLSearchParams(nextPath.split('?')[1] ?? ''), { replace: true });
}
